import * as MixpanelProfilePageActions from '@analytics/mixpanel/actions/ProfilePage';
import { TMessageType } from '@api/Booking';
import * as CelebritiesAPI from '@api/Celebrities';
import { Limiting, Pricing } from '@api/Celebrities';
import { retrieveItem, storeItem } from '@common/localstorage-helper';
import { getUserData } from '@common/utils';
import cookie from 'cookie';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { GetOrder } from './GetOrder/GetOrder';
import LimitedRequests from './LimitedRequests/LimitedRequests';
import { NotifyMe } from './NotifyMe/NotifyMe';
import NotifyMeModal from './NotifyMeModal/NotifyMeModal';
import { PriceBadge } from './PriceBadge/PriceBadge';
import { PriceText } from './PriceText/PriceText';
import { I18N_PROFILE_CONTEXT, INSTRUCTION, TEXT_ONE, TEXT_TWO } from './ProfileForm.constants';
import {
  ProfileFormContainer,
  ProfileFormDivider,
  ProfileFormFooterContainer,
  ProfileFormFooterText,
  ProfileFormFooterTextIcon,
  ProfileFormInfoContainer,
  ProfileFormInfoTopContainer,
  ProfileFormMessage,
  ProfileFormName,
  ProfileFormTabContainer,
} from './ProfileForm.styles';
import { ProfileFormProps } from './ProfileForm.types';
import ProfileFormDescription from './ProfileFormDescription/ProfileFormDescription';
import { Sharing } from './Sharing/Sharing';
import { SoldOutModal } from './SoldOutModal/SoldOutModal';
import { TabButton } from './TabButton/TabButton';

const ProfileForm = (props: ProfileFormProps): JSX.Element => {
  const router = useRouter();
  const user = props.user;
  const { t } = useTranslation(I18N_PROFILE_CONTEXT);
  const [activeTab, setActiveTab] = useState<TMessageType>(props.celebrityInfo.videoPricing.enabled ? 'video' : 'audio');
  const [activePricing, setActivePricing] = useState<Pricing>(
    props.celebrityInfo.videoPricing.enabled ? props.celebrityInfo.videoPricing : props.celebrityInfo.audioPricing,
  );
  const [activeLimiting, setActiveLimiting] = useState<Limiting>(
    props.celebrityInfo.videoPricing.enabled ? props.celebrityInfo.videoLimiting : props.celebrityInfo.audioLimiting,
  );
  const [notifyMeModal, setNotifyMeModal] = useState<{ display: boolean; email?: string; name?: string }>({ display: false });
  const [soldOutModal, setSoldOutModal] = useState<{ display: boolean; messageType?: 'audio' | 'video' | 'both' }>({ display: false });
  const [isNotified, setIsNotified] = useState<{ video: boolean; audio: boolean }>({
    audio: retrieveItem(`notify-me-for-${props.celebrityInfo.slug}-audio`) != null,
    video: retrieveItem(`notify-me-for-${props.celebrityInfo.slug}-video`) != null,
  });

  const onPricingBadgeClick = () => {
    MixpanelProfilePageActions.onMessageTypeRemainingClicked({
      router,
      details: {
        messageType: activeTab,
        celebrityName: props.celebrityInfo.slug,
        total: `
        ${
          (props.celebrityInfo.audioLimiting.enabled ? props.celebrityInfo.audioLimiting.totalSlots : 0) +
          (props.celebrityInfo.videoLimiting.enabled ? props.celebrityInfo.videoLimiting.totalSlots : 0)
        }`,
        videoRemaining: `${props.celebrityInfo.videoLimiting.remainingSlots}`,
        audioRemaining: `${props.celebrityInfo.audioLimiting.remainingSlots}`,
      },
    });
  };

  const onTabChange = (tab: TMessageType) => {
    if (tab === activeTab) return;

    setActiveTab(tab);

    let price = '';
    if (tab === 'audio') {
      price = `${props.celebrityInfo.audioPricing.currencySymbol}${props.celebrityInfo.audioPricing.price}`;
      setActivePricing(props.celebrityInfo.audioPricing);
      setActiveLimiting(props.celebrityInfo.audioLimiting);
    }

    if (tab === 'video') {
      price = `${props.celebrityInfo.videoPricing.currencySymbol}${props.celebrityInfo.videoPricing.price}`;
      setActivePricing(props.celebrityInfo.videoPricing);
      setActiveLimiting(props.celebrityInfo.videoLimiting);
    }

    MixpanelProfilePageActions.onMessageTypeTabChanged({
      router,
      details: { messageType: tab, celebrityName: props.celebrityInfo.slug, price: price },
    });
  };

  const onNotifyMe = async (email: string | undefined = undefined, messageTypes: TMessageType[] = []) => {
    const response = await CelebritiesAPI.createCelebrityNotification(props.celebrityInfo.slug, {
      email,
      messageTypes: messageTypes,
    });

    MixpanelProfilePageActions.onNotifyMeAPICalled({
      router,
      details: {
        email: response.data?.email || '',
        messageTypes: messageTypes,
        celebrityName: props.celebrityInfo.slug,
      },
    });

    if (!response.success || !response.data) return;

    setIsNotified({
      audio: messageTypes.includes('audio') || isNotified.audio,
      video: messageTypes.includes('video') || isNotified.video,
    });

    messageTypes.forEach(type => {
      if (user) {
        const resetsInSeconds =
          type === 'audio' ? props.celebrityInfo.audioLimiting.resetsInSeconds : props.celebrityInfo.videoLimiting.resetsInSeconds;
        storeItem(
          `notify-me-for-${props.celebrityInfo.slug}-${type}`,
          { messageType: type, creator: props.celebrityInfo.slug },
          resetsInSeconds,
        );
      }
    });

    setSoldOutModal({ display: false });
    setNotifyMeModal({ display: true, email: response.data.email, name: user?.name });
  };

  useEffect(() => {
    const { unavailableRequest } = cookie.parse(document.cookie);
    document.cookie = 'unavailableRequest= ; expires = Thu, 01 Jan 1970 00:00:00 GMT; path=/'; //didn't like this but we should change it in the next iteration

    if (!unavailableRequest || unavailableRequest === 'error') return;

    const user = getUserData(document.cookie);

    if (!user) return;

    setSoldOutModal({ display: true, messageType: unavailableRequest as 'audio' | 'video' | 'both' });
  }, []);

  useEffect(() => {
    activeTab == 'video' ? setActivePricing(props.celebrityInfo.videoPricing) : setActivePricing(props.celebrityInfo.audioPricing);
  }, [props]);

  return (
    <>
      {notifyMeModal.display && notifyMeModal.email && (
        <NotifyMeModal
          email={notifyMeModal.email}
          name={notifyMeModal.name}
          display={notifyMeModal.display}
          onClose={() => setNotifyMeModal({ display: false })}
        />
      )}
      {soldOutModal.display && soldOutModal.messageType && (
        <SoldOutModal
          display={soldOutModal.display}
          creatorName={props.celebrityInfo.fullname}
          creatorSlug={props.celebrityInfo.slug}
          messageType={soldOutModal.messageType}
          onClose={() => setSoldOutModal({ display: false })}
          onNotify={types => onNotifyMe(undefined, types)}
        />
      )}
      <ProfileFormContainer>
        <ProfileFormName>{props.celebrityInfo.fullname}</ProfileFormName>
        <LimitedRequests {...props.celebrityInfo}></LimitedRequests>
        <ProfileFormDescription>{props.celebrityInfo.description}</ProfileFormDescription>
        <ProfileFormDivider margin />
        <ProfileFormMessage>{t(INSTRUCTION)}</ProfileFormMessage>
        <ProfileFormTabContainer>
          {props.celebrityInfo.videoPricing.enabled && (
            <TabButton type={'video'} onClick={() => onTabChange('video')} active={activeTab === 'video'} />
          )}
          {props.celebrityInfo.audioPricing.enabled && (
            <TabButton type={'audio'} onClick={() => onTabChange('audio')} active={activeTab === 'audio'} />
          )}
        </ProfileFormTabContainer>
        <ProfileFormInfoContainer>
          <ProfileFormInfoTopContainer>
            <PriceText {...activePricing} />
            <PriceBadge {...activeLimiting} onClick={onPricingBadgeClick} />
          </ProfileFormInfoTopContainer>
          <ProfileFormDivider margin={false} />
          {activeLimiting.soldOut && (
            <NotifyMe
              activeTab={activeTab}
              slug={props.celebrityInfo.slug}
              creatorName={props.celebrityInfo.fullname}
              isLoggedIn={user != null}
              isNotified={isNotified[activeTab]}
              resetsInSeconds={activeLimiting.resetsInSeconds}
              notifyMe={email => onNotifyMe(email, [activeTab])}
            ></NotifyMe>
          )}
          {!activeLimiting.soldOut && (
            <GetOrder
              pricing={activePricing}
              creatorSlug={props.celebrityInfo.slug}
              creatorName={props.celebrityInfo.fullname}
              messageType={activeTab}
              user={props.user}
            />
          )}
          <ProfileFormDivider margin={false} />
          <Sharing
            creatorSlug={props.celebrityInfo.slug}
            videoLimiting={props.celebrityInfo.videoLimiting}
            audioLimiting={props.celebrityInfo.videoLimiting}
          />
        </ProfileFormInfoContainer>
        <ProfileFormDivider margin />
        <ProfileFormFooterContainer>
          <ProfileFormFooterText>
            <ProfileFormFooterTextIcon />
            {t(TEXT_ONE)}
          </ProfileFormFooterText>
          <ProfileFormFooterText>
            <ProfileFormFooterTextIcon />
            {t(TEXT_TWO)}
          </ProfileFormFooterText>
        </ProfileFormFooterContainer>
      </ProfileFormContainer>
    </>
  );
};

export default ProfileForm;
